import { accountKeys } from "@/features/accounts/hooks/query-keys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { financeDashboardKeys } from "@/features/finance/hooks/query-keys";
import { purchaseOrderKeys } from "@/features/purchase-orders/hooks/query-keys";
import { dashboardKeys } from "@/features/dashboard/hooks/query-keys";
import { paymentKeys } from "@/features/vendor-payments/hooks/query-keys";
import { vendorCreditKeys } from "@/features/vendor-credits/hooks/query-keys";
import {
  deleteVendorPayment,
  getVendorPaymentById,
  getVendorPayments,
  getVendorPaymentTotals,
  updateVendorPayment,
} from "@/features/vendor-payments/hooks/vendor-payments-client";
import { getVendorPaymentRelated } from "@/features/vendor-payments/hooks/vendor-payment-related";
import type {
  VendorPayment,
  VendorPaymentFilters,
  VendorPaymentUpdateInput,
} from "@/features/vendor-payments/types";

export function useVendorPayment(paymentId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: paymentKeys.vendor.detail(paymentId),
    queryFn: () => getVendorPaymentById(paymentId),
    enabled: options?.enabled !== false && Boolean(paymentId),
  });
}

/**
 * The relationship graph hanging off an already-loaded payment.
 *
 * Takes the payment rather than its id so the graph is built from the row
 * `useVendorPayment` already has. Passing an id meant this query refetched the
 * same payment under a different key, where TanStack Query's deduplication
 * could not see it — two reads of one row on every detail and edit view.
 */
export function useVendorPaymentRelated(payment: VendorPayment | undefined) {
  return useQuery({
    queryKey: paymentKeys.vendor.related(payment?.id ?? ""),
    queryFn: () => getVendorPaymentRelated(payment as VendorPayment),
    enabled: Boolean(payment),
  });
}

export function useVendorPayments(filters: VendorPaymentFilters = {}) {
  const normalizedFilters: VendorPaymentFilters = {
    ...filters,
    limit: filters.limit ?? 50,
    offset: Math.max(0, filters.offset ?? 0),
  };

  const queryKeyFilters: Record<string, unknown> = {
    limit: normalizedFilters.limit,
    offset: normalizedFilters.offset,
  };

  if (normalizedFilters.purchase_order_id) {
    queryKeyFilters.purchase_order_id = normalizedFilters.purchase_order_id;
  }
  if (normalizedFilters.account_id) {
    queryKeyFilters.account_id = normalizedFilters.account_id;
  }
  if (normalizedFilters.search?.trim()) {
    queryKeyFilters.search = normalizedFilters.search.trim();
  }
  if (normalizedFilters.sort_field) {
    queryKeyFilters.sort_field = normalizedFilters.sort_field;
    queryKeyFilters.sort_direction = normalizedFilters.sort_direction ?? "desc";
  }

  return useQuery({
    queryKey: paymentKeys.vendor.list(queryKeyFilters),
    queryFn: () => getVendorPayments(normalizedFilters),
  });
}

/**
 * Count and total paid for the whole filtered set.
 *
 * Paging and ordering are deliberately excluded from the key: the totals
 * describe the whole filtered set, so including them would refetch an
 * identical aggregate on every page step and cache it under a different key
 * each time.
 */
export function useVendorPaymentTotals(filters: VendorPaymentFilters = {}) {
  const totalsFilters: VendorPaymentFilters = {
    account_id: filters.account_id,
    purchase_order_id: filters.purchase_order_id,
    search: filters.search,
  };

  return useQuery({
    queryKey: paymentKeys.vendor.totals(totalsFilters as Record<string, unknown>),
    queryFn: () => getVendorPaymentTotals(totalsFilters),
  });
}

/**
 * Invalidations shared by the vendor payment write paths. Both move cash-out
 * and payables, so the purchase-order, dashboard, and finance-dashboard caches
 * are stale in the same way. `["dashboard"]` does not prefix-match
 * `["finance-dashboard"]`, so both have to be listed.
 *
 * `vendorCreditKeys.all` is here because an overpayment banks the remainder as
 * a `gl_vendor_credits` row linked by `source_vendor_payment_id`, and
 * `xan_delete_vendor_payment` reconciles that credit in the same transaction.
 * Leaving credits cached shows a vendor credit that the delete already removed.
 *
 * `accountKeys.all` covers the vendor's account page, which caches its
 * payments under `accountKeys.detail`.
 */
function vendorPaymentWriteInvalidations(
  queryClient: ReturnType<typeof useQueryClient>,
  variables: { purchaseOrderId?: string | null },
): Promise<unknown>[] {
  const invalidations: Promise<unknown>[] = [
    queryClient.invalidateQueries({ queryKey: paymentKeys.vendor.all }),
    queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all }),
    queryClient.invalidateQueries({ queryKey: vendorCreditKeys.all }),
    queryClient.invalidateQueries({ queryKey: accountKeys.all }),
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
    queryClient.invalidateQueries({ queryKey: financeDashboardKeys.all }),
  ];

  if (variables.purchaseOrderId) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: purchaseOrderKeys.detail(variables.purchaseOrderId),
      }),
    );
  }

  return invalidations;
}

interface DeleteVendorPaymentVariables {
  paymentId: string;
  purchaseOrderId?: string | null;
}

export function useDeleteVendorPaymentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ paymentId }: DeleteVendorPaymentVariables) => deleteVendorPayment(paymentId),
    onSuccess: async (_, variables) => {
      await Promise.all(vendorPaymentWriteInvalidations(queryClient, variables));
    },
  });
}

interface UpdateVendorPaymentVariables {
  input: VendorPaymentUpdateInput;
  paymentId: string;
  purchaseOrderId?: string | null;
}

/**
 * Corrects a recorded payment in place. `paymentKeys.vendor.all` prefix-matches
 * this payment's detail key and the relationship graph nested under it, so
 * neither needs naming on top of the shared write invalidations.
 */
export function useUpdateVendorPaymentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ input, paymentId }: UpdateVendorPaymentVariables) =>
      updateVendorPayment(paymentId, input),
    onSuccess: async (_, variables) => {
      await Promise.all(vendorPaymentWriteInvalidations(queryClient, variables));
    },
  });
}
